import React, { useState, useEffect } from "react";
import API_URL from "../../../const";
import "boxicons";
import {
  MapPin,
  Building2,
  Briefcase,
  Search,
  Filter,
  ChevronDown,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

// Mapa de estados y sus municipios (fuera del componente para evitar recrearlo cada render)
const municipiosPorEstado = {
  "CIUDAD DE MÉXICO": [
    "Álvaro Obregón",
    "Azcapotzalco",
    "Benito Juárez",
    "Coyoacán",
    "Cuajimalpa",
    "Cuauhtémoc",
    "Gustavo A. Madero",
    "Iztacalco",
    "Iztapalapa",
    "Magdalena Contreras",
    "Miguel Hidalgo",
    "Milpa Alta",
    "Tláhuac",
    "Tlalpan",
    "Venustiano Carranza",
    "Xochimilco",
  ],
  "ESTADO DE MÉXICO": [
    "Toluca",
    "Ecatepec",
    "Nezahualcóyotl",
    "Naucalpan",
    "Tlalnepantla",
    "Chimalhuacán",
    "Cuautitlán Izcalli",
    "Atizapán de Zaragoza",
    "Tultitlán",
    "Ixtapaluca",
    "Nicolás Romero",
    "Tecámac",
    "Valle de Chalco",
    "Chalco",
    "Coacalco",
    "Huixquilucan",
    "Texcoco",
  ],
};

export default function Cards({ Buscar, isAuthorized }) {
  const [userData, setUserData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGiro, setFilterGiro] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [filterMunicipio, setFilterMunicipio] = useState("");
  const [municipiosDisponibles, setMunicipiosDisponibles] = useState([]);
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(true);

  // Actualizar municipios disponibles cuando cambia el estado
  useEffect(() => {
    if (filterEstado && municipiosPorEstado[filterEstado]) {
      setMunicipiosDisponibles(municipiosPorEstado[filterEstado]);
      setFilterMunicipio("");
    } else {
      const todosMunicipios = Object.values(municipiosPorEstado).flat();
      setMunicipiosDisponibles(todosMunicipios);
    }
  }, [filterEstado]);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${API_URL}getEmpresas.php`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const startIndex = text.indexOf("[{");
        const endIndex = text.lastIndexOf("}]") + 2;
        let jsonResponse = text;

        if (startIndex !== -1 && endIndex > startIndex) {
          jsonResponse = text.substring(startIndex, endIndex);
        }

        try {
          const responseData = JSON.parse(jsonResponse);

          if (Array.isArray(responseData)) {
            setUserData(responseData);
            setFilteredData(responseData);
          } else {
            throw new Error("La respuesta no es un array válido");
          }
        } catch (error) {
          console.error("Error al analizar JSON:", error);
          setError("Error de formato en la respuesta del servidor");
          setUserData([]);
          setFilteredData([]);
        }
      })
      .catch((error) => {
        console.error("Error al cargar empresas:", error);
        setError(error.message || "Error al cargar empresas");
        setUserData([]);
        setFilteredData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Filtrar datos cuando cambie el término de búsqueda o los filtros
  useEffect(() => {
    if (!userData || userData.length === 0) return;

    let results = [...userData];

    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      results = results.filter(
        (user) =>
          user.nombre?.toLowerCase().includes(searchLower) ||
          user.giro?.toLowerCase().includes(searchLower) ||
          (user.descripcion &&
            user.descripcion.toLowerCase().includes(searchLower)),
      );
    }

    if (filterGiro !== "") {
      results = results.filter(
        (user) => user.giro?.toLowerCase() === filterGiro.toLowerCase(),
      );
    }

    if (filterEstado !== "") {
      results = results.filter(
        (user) => user.estado?.toLowerCase() === filterEstado.toLowerCase(),
      );
    }

    if (filterMunicipio !== "") {
      results = results.filter(
        (user) =>
          user.municipio?.toLowerCase() === filterMunicipio.toLowerCase(),
      );
    }

    setFilteredData(results);
  }, [searchTerm, filterGiro, filterEstado, filterMunicipio, userData]);

  // Skeleton loader para las cards
  const SkeletonCard = () => (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-5">
        <div className="h-6 bg-gray-200 rounded-lg w-3/4 mb-3"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header con título */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Catálogo de Empresas
        </h1>
        <p className="text-gray-500">
          Explora y conecta con las mejores empresas del club
        </p>
      </div>

      {/* Barra de búsqueda y filtros mejorada */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Búsqueda principal */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200 text-sm"
                placeholder="Buscar por nombre, giro o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                className="appearance-none pl-10 pr-10 py-3.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm cursor-pointer min-w-[160px]"
                value={filterGiro}
                onChange={(e) => setFilterGiro(e.target.value)}
              >
                <option value="">Todos los Giros</option>
                <option value="servicios">Servicios</option>
                <option value="tecnologia">Tecnología</option>
                <option value="industrial">Industrial</option>
                <option value="construccion">Construcción</option>
                <option value="otro">Otro</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>

            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                className="appearance-none pl-10 pr-10 py-3.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm cursor-pointer min-w-[180px]"
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
              >
                <option value="">Todos los Estados</option>
                <option value="CIUDAD DE MÉXICO">Ciudad de México</option>
                <option value="ESTADO DE MÉXICO">Estado de México</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>

            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                className="appearance-none pl-10 pr-10 py-3.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm cursor-pointer min-w-[180px]"
                value={filterMunicipio}
                onChange={(e) => setFilterMunicipio(e.target.value)}
                disabled={!filterEstado && municipiosDisponibles.length === 0}
              >
                <option value="">Todos los Municipios</option>
                {municipiosDisponibles.map((municipio, index) => (
                  <option key={index} value={municipio}>
                    {municipio}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Contador de resultados */}
        {!isLoading && !error && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Mostrando{" "}
              <span className="font-semibold text-gray-700">
                {filteredData.length}
              </span>{" "}
              de{" "}
              <span className="font-semibold text-gray-700">
                {userData.length}
              </span>{" "}
              empresas
            </p>
          </div>
        )}
      </div>

      {/* Grid de tarjetas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {isLoading ? (
          // Skeleton loaders mientras carga
          <>
            {[...Array(8)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </>
        ) : error ? (
          // Estado de error
          <div className="col-span-full">
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Error al cargar empresas
              </h3>
              <p className="text-gray-500 text-center mb-6 max-w-md">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Reintentar
              </button>
            </div>
          </div>
        ) : filteredData.length > 0 ? (
          // Cards de empresas
          filteredData.map((empresa) => (
            <div
              key={empresa.id || empresa.uuid}
              onClick={() => {
                if (Buscar && isAuthorized && empresa.uuid) {
                  Buscar(empresa.uuid);
                } else if (!isAuthorized) {
                  console.warn("Usuario no autorizado");
                } else if (!empresa.uuid) {
                  console.error("UUID de empresa no disponible");
                }
              }}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
            >
              {/* Imagen de portada o logo */}
              <div className="relative h-48 bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
                <img
                  src={`${API_URL}${empresa.portada || empresa.logo}`}
                  alt={empresa.nombre}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                {/* Placeholder si no hay imagen */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 items-center justify-center hidden">
                  <Building2 className="w-16 h-16 text-white/80" />
                </div>

                {/* Badge de giro */}
                {empresa.giro && (
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm">
                      {empresa.giro}
                    </span>
                  </div>
                )}

                {/* Logo pequeño superpuesto */}
                {empresa.logo && (
                  <div className="absolute bottom-3 left-4">
                    <div className="w-14 h-14 rounded-xl bg-white shadow-lg border-2 border-white overflow-hidden">
                      <img
                        src={`${API_URL}${empresa.logo}`}
                        alt=""
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.parentNode.innerHTML = `<div class="w-full h-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">${
                            empresa.nombre?.charAt(0) || "E"
                          }</div>`;
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Contenido de la card */}
              <div className="p-5">
                <h3 className="font-bold text-gray-800 text-lg mb-2 group-hover:text-blue-600 transition-colors line-clamp-1">
                  {empresa.nombre}
                </h3>

                {empresa.especialidad && (
                  <p className="text-sm text-gray-500 mb-3 line-clamp-1">
                    {empresa.especialidad}
                  </p>
                )}

                {/* Info adicional */}
                <div className="space-y-2">
                  {empresa.estado && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="line-clamp-1">
                        {empresa.municipio ? `${empresa.municipio}, ` : ""}
                        {empresa.estado}
                      </span>
                    </div>
                  )}
                </div>

                {/* Botón de ver más - Solo visible para usuarios autorizados */}
                {isAuthorized && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-600 group-hover:text-blue-700">
                        Ver detalles
                      </span>
                      <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                        <i className="bx bx-right-arrow-alt text-blue-600 text-lg group-hover:translate-x-0.5 transition-transform"></i>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          // Sin resultados
          <div className="col-span-full">
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                No se encontraron empresas
              </h3>
              <p className="text-gray-500 text-center max-w-md">
                Intenta ajustar los filtros de búsqueda o busca con otros
                términos
              </p>
              {(searchTerm ||
                filterGiro ||
                filterEstado ||
                filterMunicipio) && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterGiro("");
                    setFilterEstado("");
                    setFilterMunicipio("");
                  }}
                  className="mt-4 text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
